import React from "react";
import { ShieldCheck, Mail, Phone, MessageSquare } from "lucide-react";

export default function PrivacyPolicy() {
  return (
    <div data-testid="privacy-page" className="min-h-screen pb-24">
      {/* Hero */}
      <div className="bg-gradient-to-br from-amber-50 via-white to-amber-50/40 border-b border-amber-100">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-14">
          <div className="overline mb-3 text-[#B88C2E] flex items-center gap-2">
            <ShieldCheck className="w-3.5 h-3.5" /> Legal · Effective Feb 9, 2026
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-slate-900 leading-tight">
            Privacy <span className="text-[#D4A437]">Policy</span>
          </h1>
          <p className="text-slate-600 mt-4 max-w-3xl text-base sm:text-lg font-light">
            How OC Infraventures &amp; Constructions Pvt Ltd collects, uses, stores and protects the information
            you share with us.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 md:px-12 py-12 space-y-10 text-slate-700">
        <Section title="1. Information We Collect">
          <p>
            When you voluntarily send us electronic mail or fill up the form, we will keep a record
            of this information so that we can respond to you. We only collect information from you
            when you register on our site or fill out a form.
          </p>
          <p>
            Also, when filling out a form on our site, you may be asked to enter your:
          </p>
          <ul className="list-disc list-inside space-y-1.5 pl-2 text-sm">
            <li>Name</li>
            <li>E-mail address</li>
            <li>Phone number</li>
            <li>Property requirements (city, locality, budget, configuration)</li>
            <li>Any voluntary message you choose to share</li>
          </ul>
          <p>You may, however, visit our site anonymously.</p>
        </Section>

        <Section title="2. Consent to Contact (Overrides DND / NDNC)">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
            <p className="font-semibold text-slate-900 mb-2">Important — please read carefully</p>
            <p>
              In case you have submitted your personal information and contact details, we reserve
              the rights to <strong>Call, SMS, Email or WhatsApp</strong> about our products and
              offers, <strong>even if your number has DND (Do Not Disturb) activated on it</strong>.
            </p>
            <p className="mt-3">
              By voluntarily submitting an enquiry form on this website, you give us explicit
              consent (under TRAI&apos;s TCCCPR 2018 framework) to override the DND / NDNC registry for
              the purpose of responding to your enquiry and sharing relevant property updates.
            </p>
            <div className="grid sm:grid-cols-4 gap-3 mt-5">
              {[
                { icon: Phone, label: "Voice Call" },
                { icon: MessageSquare, label: "SMS" },
                { icon: MessageSquare, label: "WhatsApp" },
                { icon: Mail, label: "E-mail" },
              ].map((c) => (
                <div key={c.label} className="bg-white rounded-lg px-3 py-2.5 border border-amber-100 flex items-center gap-2 text-sm font-medium text-slate-800">
                  <c.icon className="w-4 h-4 text-[#D4A437]" /> {c.label}
                </div>
              ))}
            </div>
          </div>
          <p className="mt-4 text-sm">
            You may withdraw this consent any time by writing to{" "}
            <a href="mailto:info@ocinfraventures.com" className="text-[#B88C2E] underline decoration-dotted hover:text-[#D4A437]">
              info@ocinfraventures.com
            </a>{" "}
            with subject line <em>&ldquo;Unsubscribe&rdquo;</em>. We will action your request within 7 business days.
          </p>
        </Section>

        <Section title="3. How We Use Your Information">
          <ul className="list-disc list-inside space-y-1.5 pl-2 text-sm">
            <li>To respond to your enquiry and arrange property viewings</li>
            <li>To share information about relevant OC Infraventures projects, hot deals and new launches</li>
            <li>To offer home loan / interior / NRI services that match your stated needs</li>
            <li>To improve our website experience and the relevance of our recommendations</li>
            <li>To meet legal obligations (RERA, tax, KYC for transactions)</li>
          </ul>
        </Section>

        <Section title="4. Data Storage &amp; Security">
          <p>
            Your information is stored on encrypted MongoDB instances within Emergent cloud
            infrastructure. Passwords are hashed using bcrypt before storage. Access to enquiry
            data is restricted to authorised OC Infraventures staff only.
          </p>
          <p>
            We retain enquiry records for up to 24 months from the last contact, after which they
            are anonymised. Transactional records (executed sale agreements) are retained for the
            statutory period required by Indian tax and RERA regulations.
          </p>
        </Section>

        <Section title="5. Third-Party Sharing">
          <p>We <strong>do not sell</strong> your personal information. We may share necessary contact details with:</p>
          <ul className="list-disc list-inside space-y-1.5 pl-2 text-sm">
            <li>Developers / channel partners for the specific project you enquired about</li>
            <li>Empanelled banks / NBFCs (only if you request home loan assistance)</li>
            <li>Legal / valuation panel (only if you opt-in for paid certified services)</li>
            <li>Regulatory authorities (RERA, IT, FEMA) where legally required</li>
          </ul>
        </Section>

        <Section title="6. Cookies &amp; Analytics">
          <p>
            We use functional cookies to keep you signed in and remember your favourites. We may
            use anonymised analytics (Google Analytics / Meta Pixel) to understand site traffic
            patterns. No personally identifiable information is shared with analytics providers.
          </p>
        </Section>

        <Section title="7. Your Rights">
          <ul className="list-disc list-inside space-y-1.5 pl-2 text-sm">
            <li><strong>Access</strong> — request a copy of the data we hold about you</li>
            <li><strong>Correction</strong> — ask us to fix inaccurate data</li>
            <li><strong>Deletion</strong> — request your data be erased (subject to statutory retention)</li>
            <li><strong>Opt-out</strong> — withdraw consent for marketing communications</li>
            <li><strong>Grievance</strong> — raise a concern with our Data Protection Officer</li>
          </ul>
        </Section>

        <Section title="8. Grievance Officer">
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <p className="font-semibold text-slate-900">Data Protection Officer</p>
            <p className="text-sm mt-2">
              OC Infraventures &amp; Constructions Pvt Ltd<br />
              504B Felix Square, International Business Bay,<br />
              Sushant Golf City, Lucknow 226030, Uttar Pradesh, India<br />
              Email: <a href="mailto:info@ocinfraventures.com" className="text-[#B88C2E] underline decoration-dotted">info@ocinfraventures.com</a><br />
              Phone: +91 522 4000 800
            </p>
          </div>
        </Section>

        <Section title="9. Updates to This Policy">
          <p>
            We may update this Privacy Policy from time to time to reflect changes in law or our
            services. The &ldquo;Effective&rdquo; date at the top of this page will reflect the latest version.
            Material changes will be communicated via email to registered users.
          </p>
        </Section>

        <Section title="10. RERA &amp; Regulatory Disclosure">
          <p>
            OC Infraventures &amp; Constructions Pvt Ltd is a registered real estate agent under
            the Real Estate (Regulation and Development) Act, 2016.
          </p>
          <div className="inline-flex items-center gap-3 mt-2 px-4 py-2.5 bg-emerald-50 border border-emerald-200 rounded-lg">
            <ShieldCheck className="w-4 h-4 text-emerald-700" />
            <span className="text-sm font-mono tracking-wider text-emerald-900 font-semibold">UP RERA Agent · UPRERAAGT15084</span>
          </div>
        </Section>

        <div className="border-t border-slate-200 pt-6 text-xs text-slate-500 italic">
          Last updated: 9 February 2026 · © OC Infraventures &amp; Constructions Pvt Ltd.
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-serif text-2xl text-slate-900 mb-4">{title}</h2>
      <div className="space-y-3 text-sm sm:text-base leading-relaxed">{children}</div>
    </section>
  );
}
